import { ArrowUpRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useArticles } from "@/hooks/useArticles";
import { Skeleton } from "@/components/ui/skeleton";
import useEmblaCarousel from "embla-carousel-react";
import { useEffect, useCallback } from "react";

export const FeaturedArticles = () => {
  const navigate = useNavigate();
  const { articles, loading, error } = useArticles();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
  });

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const interval = setInterval(() => {
      scrollNext();
    }, 3000);
    return () => clearInterval(interval);
  }, [emblaApi, scrollNext]);

  const featured = articles;

  if (error) {
    return (
      <section className="bg-[#050505] text-center py-20">
        <p className="text-red-500">Could not fetch articles.</p>
      </section>
    );
  }

  return (
    <section
      id="articles"
      data-testid="featured-articles-section"
      className="relative bg-[#050505] px-6 md:px-10 lg:px-16 py-20 md:py-28"
    >
      <div className="flex items-end justify-between mb-12 md:mb-16 border-b border-white/10 pb-6 flex-wrap gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-flare mb-3">
            /02 — featured articles
          </p>
          <h2
            data-testid="featured-heading"
            className="font-display font-extrabold text-4xl md:text-6xl uppercase tracking-tighter leading-[0.95]"
          >
            Long reads. <br />
            <span className="italic font-normal">Lived</span> stories.
          </h2>
        </div>
        <Link
          to="/articles"
          data-testid="articles-see-all"
          className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] bg-flare text-black px-6 py-4 hover:bg-bone transition-colors"
        >
          Read all articles
          <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6 md:gap-8">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="relative flex-[0_0_85%] md:flex-[0_0_40%] min-w-0"
                >
                  <div className="border border-white/10 aspect-[4/3] md:aspect-[5/4]">
                    <Skeleton className="h-full w-full" />
                  </div>
                </div>
              ))
            : featured.map((a) => (
                <div
                  key={a.slug || a.id}
                  className="relative flex-[0_0_85%] md:flex-[0_0_40%] min-w-0"
                >
                  <button
                    onClick={() => navigate(`/articles/${a.slug}`)}
                    data-testid={`featured-article-${a.slug || a.id}`}
                    className="group relative text-left w-full h-full block border border-white/10"
                  >
                    <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden bg-black">
                      <img
                        src={a.cover}
                        alt={a.title}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                      <span className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] bg-flare text-black px-2 py-1">
                        {a.tag}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 pointer-events-none">
                      <h3 className="font-display font-extrabold text-2xl md:text-3xl lg:text-4xl uppercase tracking-tighter leading-[0.95] text-bone group-hover:text-flare transition-colors line-clamp-3">
                        {a.title}
                      </h3>
                      <p className="mt-4 text-bone-muted text-sm md:text-base max-w-2xl leading-relaxed line-clamp-2">
                        {a.excerpt}
                      </p>
                      <div className="mt-6 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-bone-muted">
                        <span>
                          {a.author} ·{" "}
                          <span className="text-flare">{a.read}</span>
                        </span>
                        <ArrowUpRight className="w-5 h-5 text-bone group-hover:text-flare group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </div>
                  </button>
                </div>
              ))}
        </div>
      </div>
    </section>
  );
};
